import { useCallback, useEffect, useState } from 'react';
import { Affix, message, Modal } from 'antd';
import { Button, Form, FormList, FormItem, Input, Text, Col, Row } from '@/components/atoms';
import { useParams } from 'react-router';
import api from '@/api';
import { API_OBJECTION_EDIT, API_TAX_NOTICE_INFO, FORMAT_DATE_EXCEPT_SECOND_KR, IMAGE_PATH_TAX_NOTICE } from '@/constants/constant';
import { ptax } from '@/proto/proto';
import { ErrorMessage } from '@/constants/message';
import { useSelector } from 'react-redux';
import { RootState } from '@/rootReducer';
import Title from '@/components/atoms/text/Title';
import TaxTemplate from '@/components/template/tax/TaxTemplate';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import closeIcon from '@images/icon/btn_close_p.png';
import moment from 'moment';

type PreviewModalStateProps = {
  visible: boolean;
  image?: string;
};

const TaxNoticeDetailPage = () => {
  const { taxId } = useParams<any>();
  const { current: company } = useSelector((state: RootState) => state.company);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ptax.IPbTaxNotice>();
  const [error, setError] = useState(null);

  const [form] = Form.useForm();

  const [previewState, setPreviewState] = useState<PreviewModalStateProps>({
    visible: false,
  });

  const refresh = (id: number) => {
    setLoading(true);
    api
      .get(API_TAX_NOTICE_INFO(id))
      .then((response) => {
        setData(response.data);
      })
      .catch((e) => {
        setError(e?.response?.data?.message ?? ErrorMessage.network.disconnected);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const save = useCallback(() => {
    setLoading(true);
    const violation =
      form.getFieldValue('violation')?.map((each: ptax.IPbTaxNoticeViolation) => ({
        ...each,
        violationAt: moment(each.violationAt, FORMAT_DATE_EXCEPT_SECOND_KR).toISOString(),
      })) ?? [];
    const [first] = violation;
    const newData = {
      ...data,
      violation,
      sender: form.getFieldValue('sender'),
      plateNo: form.getFieldValue('plateNo'),
      details: form.getFieldValue('details'),
    };
    if (!first) {
      setLoading(false);
      message.error('위반내역 정보를 등록하지 않은 상태에서는 저장할 수 없습니다.');
      return;
    }
    api
      .post(API_OBJECTION_EDIT, 'ptax.PbTaxNotice', newData)
      .then(() => {
        message.success('정상적으로 처리되었습니다.');
      })
      .catch((e) => {
        setError(e?.response?.data?.message ?? ErrorMessage.network.disconnected);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [data, form]);

  useEffect(() => {
    refresh(taxId);
  }, [taxId]);

  useEffect(() => {
    if (error) {
      message.error(error);
      setError(null);
    }
  }, [error]);

  useEffect(() => {
    if (data) {
      const { violation } = data;
      console.log('violation', violation);
      form.setFieldsValue({
        ...data,
        violation: violation?.map((item) => ({ ...item, violationAt: moment(item.violationAt).format(FORMAT_DATE_EXCEPT_SECOND_KR) })),
      });
    }
  }, [data, form]);

  return (
    <TaxTemplate>
      <Title>고지서 상세</Title>
      <Form layout="vertical" form={form}>
        <Row>
          <Col xl={6} xs={24} style={{ justifyContent: 'center' }}>
            <LazyLoadImage
              src={IMAGE_PATH_TAX_NOTICE(company.base.id, data)}
              onClick={() =>
                setPreviewState({
                  visible: true,
                  image: IMAGE_PATH_TAX_NOTICE(company.base.id, data),
                })
              }
            />
          </Col>
          <Col xl={18} xs={24} style={{ justifyContent: 'center', alignItems: 'center', display: 'flex' }}>
            <Row style={{ width: '100%', margin: '24px' }}>
              <FormItem label="발급기관" name="sender">
                <Input />
              </FormItem>
              <FormItem label="차량번호" name="plateNo">
                <Input />
              </FormItem>
              <FormItem label="위반내용" name="details">
                <Input />
              </FormItem>
              <FormItem label="발송일" name="sentDate">
                <Input />
              </FormItem>
              <FormItem label="납부기한" name="dueDate">
                <Input />
              </FormItem>
            </Row>
          </Col>
        </Row>
        <Row justify="space-between" className="w-full">
          <Title>위반목록</Title>
          <Button
            type="text"
            onClick={() => {
              setData({ ...data, violation: [...(data?.violation ?? []), {}] });
            }}
          >
            목록 추가
          </Button>
        </Row>
        <FormList name="violation">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field, index) => {
                return (
                  <FormItem
                    className="violation-item"
                    style={{ marginBottom: '4px' }}
                    key={field.key}
                    shouldUpdate={(prevValues, curValues) => prevValues.area !== curValues.area || prevValues.violation !== curValues.violation}
                  >
                    {() => (
                      <Row className="w-full">
                        <Col md={6} sm={24}>
                          <FormItem name={[field.name, 'violationAt']}>
                            <Input />
                          </FormItem>
                        </Col>
                        <Col flex={1}>
                          <FormItem name={[field.name, 'location']}>
                            <Input />
                          </FormItem>
                        </Col>
                        <Col md={3} sm={24}>
                          <FormItem name={[field.name, 'price']}>
                            <Input />
                          </FormItem>
                        </Col>
                        <Col span={1}>
                          {index > 0 && (
                            // eslint-disable-next-line jsx-a11y/anchor-is-valid,jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
                            <a onClick={() => remove(field.name)}>
                              <img src={closeIcon} alt="아이템 삭제" />
                            </a>
                          )}
                        </Col>
                      </Row>
                    )}
                  </FormItem>
                );
              })}
            </>
          )}
        </FormList>
        <Affix offsetBottom={10}>
          <Row justify="space-between">
            <Text>해당 내용을 저장하시겠습니까?</Text>
            <FormItem style={{ width: 'auto' }}>
              <Button loading={loading} onClick={save} htmlType="submit">
                저장
              </Button>
            </FormItem>
          </Row>
        </Affix>
      </Form>
      <Modal visible={previewState.visible} onCancel={() => setPreviewState({ ...previewState, visible: false })}>
        <LazyLoadImage src={previewState.image} />
      </Modal>
    </TaxTemplate>
  );
};

export default TaxNoticeDetailPage;
